import styled from 'styled-components';
import { Text, Input, Button, Link, LinkButton, useMq } from '@wistia/vhs';
import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { isEmptyString, isNil, isNotNil } from '~/utilities/type-guards';
import { CommentingDisabledTooltip } from '~/singlePageApp/screens/AnonymousMedia/layouts/Shared';
import { RequestToBeAUserModal } from '~/singlePageApp/components/RequestToBeAUser/RequestToBeAUserModal';
import type { NoOpFn } from '~/utilities/util';
import { commentingViews, useCommentContext } from '../CommentContext';
import { CreateCommentForm } from './CreateCommentForm';
import type { CurrentContactQuery } from './__gql/Forms.gql.gen';
import { useCurrentContactQuery } from './__gql/Forms.gql.gen';

const StickyForm = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
`;

const AnonFormWrapper = styled.div<{ isBelowMedia: boolean; showCommentForm: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 0;
  gap: ${({ theme }) => theme.spacing.space04};
  margin: ${({ isBelowMedia }) => (isBelowMedia ? '16px 0' : '16px')};
  padding: 0 ${({ theme }) => theme.spacing.space03};

  ${({ showCommentForm }) => showCommentForm && 'margin-top: 0;'}
`;

const NameSubmission = styled.div<{ showStackedView: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: ${({ showStackedView }) => (showStackedView ? 'column' : 'row')};
  gap: ${({ theme }) => theme.spacing.space04};
  justify-content: space-between;
`;

const InputWithLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.space02};
`;

const FeatureInfo = styled.div`
  background: ${({ theme }) => theme.color.grey200};
  border-radius: ${({ theme }) => theme.spacing.space01};
  padding: ${({ theme }) => theme.spacing.space03};
`;

export const AccessInfo = ({
  loginPath,
  contact,
  mediaId,
}: {
  loginPath: string;
  contact: CurrentContactQuery['currentContact'];
  mediaId: string;
}): JSX.Element => {
  return isNil(contact) ? (
    <>
      To receive comment notifications, manage this video, and tag other users,{' '}
      <RequestToBeAUserModal
        contact={contact}
        mediaId={mediaId}
        toggle={(show: NoOpFn) => (
          <LinkButton
            onClick={show}
            underlineOnHover={true}
          >
            request access
          </LinkButton>
        )}
      />{' '}
      or{' '}
      <Link
        href={loginPath}
        underlineOnHover={true}
      >
        log in to Wistia
      </Link>
      .
    </>
  ) : (
    <>
      <RequestToBeAUserModal
        contact={contact}
        mediaId={mediaId}
        toggle={(show: NoOpFn) => (
          <LinkButton
            onClick={show}
            underlineOnHover={true}
          >
            Request access
          </LinkButton>
        )}
      />{' '}
      to receive comment notifications, manage this video, and tag other users.
    </>
  );
};

export const AnonymousCommentForm = ({
  commentingDisabled,
  mediaId,
  isBelowMedia = false,
}: {
  commentingDisabled: boolean;
  mediaId: string;
  isBelowMedia?: boolean;
}): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [hasInputError, setHasInputError] = useState<boolean>(false);
  const { anonUserName, setAnonUserName, commentingView } = useCommentContext();
  const { data } = useCurrentContactQuery();
  const { isXsAndDown } = useMq();

  const showCommentForm = isNotNil(anonUserName);
  const showStackedView = isXsAndDown || !isBelowMedia;

  const { host, href } = window.location;
  const loginPath = `https://${host}/login?redirect_to=${encodeURIComponent(href)}`;

  const parentId = commentingView === commentingViews.audience ? mediaId : null;

  const onClick = () => {
    const name = inputRef.current?.value;

    if (isNil(name) || isEmptyString(name.trim())) {
      setHasInputError(true);
    } else {
      setHasInputError(false);
      setAnonUserName(name);
    }
  };

  return (
    <StickyForm>
      <AnonFormWrapper
        isBelowMedia={isBelowMedia}
        showCommentForm={showCommentForm}
      >
        {showCommentForm ? (
          <CreateCommentForm
            contact={null}
            isReply={false}
            isRightOfPlayer={!isBelowMedia}
            mediaId={mediaId}
            parentId={parentId}
            commentingDisabled={commentingDisabled}
          />
        ) : (
          <>
            <InputWithLabel>
              <Text variant="subtitle2">Join the conversation</Text>
              <NameSubmission showStackedView={showStackedView}>
                <Input
                  ref={inputRef}
                  placeholder="Name*"
                  fullWidth={true}
                  hasError={hasInputError}
                  data-testid="anon-user-input"
                  disabled={commentingDisabled}
                />
                <CommentingDisabledTooltip disabled={commentingDisabled}>
                  <Button
                    label="Start Commenting"
                    onClick={onClick}
                    disabled={commentingDisabled}
                    fullWidth={showStackedView}
                  />
                </CommentingDisabledTooltip>
              </NameSubmission>
            </InputWithLabel>
            <FeatureInfo>
              <Text variant="body2">
                {commentingView === commentingViews.audience ? (
                  <>
                    Have a Wistia account?{' '}
                    <Link
                      href={loginPath}
                      underlineOnHover={true}
                    >
                      Sign in
                    </Link>
                  </>
                ) : (
                  <AccessInfo
                    contact={data?.currentContact}
                    loginPath={loginPath}
                    mediaId={mediaId}
                  />
                )}
              </Text>
            </FeatureInfo>
          </>
        )}
      </AnonFormWrapper>
    </StickyForm>
  );
};
